
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Libraries from 'plaid-threads/Icons/Libraries';
import Guide from 'plaid-threads/Icons/Guide';
import Globe from 'plaid-threads/Icons/Globe';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  toc: true,
  layout: 'guide',
  section: 'Payments',
  title: 'Introduction',
  description: 'Initiate real-time payments for account funding and transfers'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Overview.Header product={'Payments (Europe)'}>
  {metadata.description}
    </Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Payments (Europe)'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/payment-initiation/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Integration Guide' link='/docs/payment-initiation/payment-initiation-one-time/add-to-app' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Plaid's European Payments suite enables your users to make real-time payments without manually entering their account details or leaving your app. Common use cases include:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Account funding:`}</strong>{` Allow users to easily transfer money into your app or wallet.`}</li>
      <li parentName="ul"><strong parentName="li">{`Pay-by-bank:`}</strong>{` Payments supports both one-time payments and (in the UK) recurring payments. Accept payments, track settlement, and issue refunds.`}</li>
      <li parentName="ul"><strong parentName="li">{`Payouts:`}</strong>{` Pay out stored balances to your end users.`}</li>
    </ul>
    <Image src='/assets/img/docs/payment_initiation/payment-initiation-uk-link-screens.png' alt="Plaid Link payment flow: view payment details, select bank, authenticate with Gingham Bank, confirm payment with details." expandable={true} mdxType="Image" />
    <p>{`Benefits of Payments include:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Easy integration with a single API:`}</strong>{` Verify user accounts, manage risk, and move money across 18 European markets, all through a single Plaid integration.`}</li>
      <li parentName="ul"><strong parentName="li">{`Real-time settlement, simplified reconciliation:`}</strong>{` Sweep transaction funds into your treasury account quickly and balance your books with an intuitive reconciliation report.`}</li>
      <li parentName="ul"><strong parentName="li">{`Multi-rail support:`}</strong>{` Payments supports Faster Payments Service (FPS), SEPA Instant Credit Transfer, SEPA Credit Transfer and local country payment rails. Easily switch payment rails with a single request parameter, no complex code changes required.`}</li>
      <li parentName="ul"><strong parentName="li">{`Streamlined operational support:`}</strong>{` Manage daily operations with the Plaid Dashboard to easily monitor payment activity.`}</li>
    </ul>
    <p>{`For enhanced capabilities, you can add the optional `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/virtual-accounts/"
      }}>{`Virtual Accounts`}</a>{` feature to unlock advanced features such as sending refunds to users, allowing your users to pay out a credit balance to a bank account, or gaining granular visibility into the settlement status of a payment.`}</p>
    <Callout icon={<Globe mdxType="Globe" />} mdxType="Callout">
      <p>{`Payments is Europe-only. Looking for similar capabilities in the US? Check out the `}<a parentName="p" {...{
          "href": "/docs/transfer"
        }}>{`Transfer`}</a>{` docs.`}</p>
    </Callout>
    <h4 {...{
      "id": "payments-solutions"
    }}>{`Payments solutions`}</h4>
    <p>{`The Payments suite includes the following solutions. You can "mix and match" these solutions, incorporating all three for a robust, fully-integrated solution, or purchase them separately, incorporating only the ones you need. (Exception: Payouts requires Virtual Accounts and cannot be used separately.)`}</p>
    <p><strong parentName="p"><a parentName="strong" {...{
          "href": "/docs/payment-initiation/payment-initiation-one-time/"
        }}>{`One-time Payment Initiation`}</a></strong>{`: Enable your users to make single real-time payments, such as SEPA Instant Credit Transfers or Single Immediate Payments (UK), without manually entering their account number and sort code.`}</p>
    <p><strong parentName="p"><a parentName="strong" {...{
          "href": "/docs/payment-initiation/variable-recurring-payments/"
        }}>{`Variable Recurring Payments (VRP)`}</a></strong>{` `}<em parentName="p">{`(UK only)`}</em>{`: Establish a single recurring payment consent, which can then be used for a series of ongoing payments, with no end user interaction required. For background information on what Variable Recurring Payments (VRPs) are and how they work in the UK banking system, see the `}<a parentName="p" {...{
        "href": "https://support.plaid.com/hc/en-us/articles/24622039958295-What-are-Variable-Recurring-Payments-VRPs"
      }}>{`Plaid VRP FAQ`}</a>{`. For instructions on integrating support for Variable Recurring Payments using Plaid, see `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/variable-recurring-payments/add-to-app/"
      }}>{`Add Variable Recurring Payments to your app`}</a>{`. `}</p>
    <p><strong parentName="p"><a parentName="strong" {...{
          "href": "/docs/payment-initiation/virtual-accounts/payouts"
        }}>{`Payouts`}</a></strong>{` (requires Virtual Accounts add-on): Payouts enables users to seamlessly and instantly make withdrawals or get paid. `}</p>
    <p>{`Any of the solutions above can also be enhanced with the Virtual Accounts add-on:`}</p>
    <p><strong parentName="p"><a parentName="strong" {...{
          "href": "/docs/payment-initiation/virtual-accounts"
        }}>{`Virtual Accounts`}</a></strong>{`: Collect payments, get confirmation when payments settle, initiate payouts and refunds, and streamline reconciliation processes. Virtual Accounts provides granular control and visibility throughout the entire lifecycle of a payment.`}</p>
    <p>{`Payments can also be used with other Plaid solutions such as `}<a parentName="p" {...{
        "href": "/docs/auth"
      }}>{`Auth`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/identity"
      }}>{`Identity`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/identity-verification"
      }}>{`Identity Verification`}</a>{`, and `}<a parentName="p" {...{
        "href": "/docs/monitor"
      }}>{`Monitor`}</a>{` for enhanced anti-fraud protections and KYC/AML compliance.`}</p>
    <h4 {...{
      "id": "onboarding-and-account-funding-integration-guide"
    }}>{`Onboarding and account funding integration guide`}</h4>
    <p>{`See `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/payment-initiation-one-time/#onboarding-and-account-funding-integration-guide"
      }}>{`User onboarding and account funding`}</a>{` for an implementation guide to using Plaid's Europe Payments suite as part of a KYC and AML compliant onboarding flow.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "payments-solutions",
        level: 4,
        title: "Payments solutions",
      },
{
        id: "onboarding-and-account-funding-integration-guide",
        level: 4,
        title: "Onboarding and account funding integration guide",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
